import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as REPointerEvent,
} from 'react';
import type { LauncherPosition } from '@teo/widget-shared';
import { useWidget } from '../context/WidgetContext.js';
import { AvatarImage } from '../components/Avatar.js';
import { IconChat } from '../components/icons.js';
import {
  clamp,
  EDGE_STORAGE_PREFIX,
  edgeFromPosition,
  isTopPosition,
  originStorageSuffix,
  viewportSize,
  type Edge,
  type LauncherRect,
} from './launcher-geometry.js';

const DRAG_THRESHOLD_PX = 8;
const DOCK_TAB_W = 40;
const DOCK_TAB_H = 44;

type DockState = {
  edge: Edge;
  offsetY: number;
};

type EdgeDockTabProps = {
  position: LauncherPosition;
  offsetY: number;
  unreadCount?: number;
  onOpen: () => void;
  onRectChange?: (rect: LauncherRect) => void;
  ariaLabel?: string;
};

function clampOffsetY(y: number): number {
  const { h } = viewportSize();
  return clamp(y, 8, h - DOCK_TAB_H - 8);
}

function defaultOffsetY(position: LauncherPosition, offsetY: number): number {
  const { h } = viewportSize();
  if (isTopPosition(position)) {
    return clampOffsetY(offsetY + 48);
  }
  return clampOffsetY(h - DOCK_TAB_H - offsetY - 24);
}

function defaultDock(position: LauncherPosition, offsetY: number): DockState {
  return {
    edge: edgeFromPosition(position),
    offsetY: defaultOffsetY(position, offsetY),
  };
}

function readStored(position: LauncherPosition, offsetY: number): DockState {
  try {
    const raw = localStorage.getItem(EDGE_STORAGE_PREFIX + originStorageSuffix());
    if (!raw) return defaultDock(position, offsetY);
    const parsed = JSON.parse(raw) as DockState & { settingsEdge?: string };
    if (
      (parsed.edge !== 'left' && parsed.edge !== 'right') ||
      typeof parsed.offsetY !== 'number'
    ) {
      return defaultDock(position, offsetY);
    }
    return { edge: parsed.edge, offsetY: clampOffsetY(parsed.offsetY) };
  } catch {
    return defaultDock(position, offsetY);
  }
}

export function EdgeDockTab({
  position,
  offsetY,
  unreadCount = 0,
  onOpen,
  onRectChange,
  ariaLabel = 'Open chat',
}: EdgeDockTabProps) {
  const { settings } = useWidget();
  const settingsEdge = edgeFromPosition(position);
  const [dock, setDock] = useState<DockState>(() => readStored(position, offsetY));
  const dockRef = useRef(dock);
  dockRef.current = dock;
  const [avatarFailed, setAvatarFailed] = useState(false);
  const avatarUrl = settings.content.aiAvatarUrl?.trim() || null;
  const showAvatar = Boolean(avatarUrl) && !avatarFailed;

  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origOffsetY: number;
    moved: boolean;
  } | null>(null);

  const emitRect = useCallback(
    (d: DockState) => {
      const { w } = viewportSize();
      onRectChange?.({
        x: d.edge === 'left' ? 0 : w - DOCK_TAB_W,
        y: d.offsetY,
        w: DOCK_TAB_W,
        h: DOCK_TAB_H,
      });
    },
    [onRectChange],
  );

  useEffect(() => {
    const next = readStored(position, offsetY);
    setDock(next);
    dockRef.current = next;
    emitRect(next);
  }, [position, offsetY, emitRect]);

  useEffect(() => {
    emitRect(dock);
  }, [dock, emitRect]);

  useEffect(() => {
    const onResize = () => {
      setDock((d) => {
        const next = { ...d, offsetY: clampOffsetY(d.offsetY) };
        dockRef.current = next;
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, []);

  const persist = useCallback(
    (next: DockState) => {
      const normalized = { ...next, offsetY: clampOffsetY(next.offsetY) };
      setDock(normalized);
      dockRef.current = normalized;
      try {
        localStorage.setItem(
          EDGE_STORAGE_PREFIX + originStorageSuffix(),
          JSON.stringify({ ...normalized, settingsEdge }),
        );
      } catch {
        /* ignore */
      }
      emitRect(normalized);
    },
    [emitRect, settingsEdge],
  );

  const onPointerDown = (e: REPointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origOffsetY: dockRef.current.offsetY,
      moved: false,
    };
  };

  const onPointerMove = (e: REPointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    d.moved = true;
    setDock((prev) => {
      const next = { ...prev, offsetY: clampOffsetY(d.origOffsetY + dy) };
      dockRef.current = next;
      emitRect(next);
      return next;
    });
  };

  const onPointerUp = (e: REPointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!d.moved) {
      onOpen();
      return;
    }
    const { w } = viewportSize();
    const nearest: Edge = e.clientX < w / 2 ? 'left' : 'right';
    const edge = Math.abs(e.clientX - d.startX) < 40 ? settingsEdge : nearest;
    persist({
      edge,
      offsetY: clampOffsetY(d.origOffsetY + (e.clientY - d.startY)),
    });
  };

  return (
    <button
      type="button"
      className={`teo-edge-dock teo-edge-dock--${dock.edge}`}
      style={{ top: dock.offsetY }}
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {showAvatar ? (
        <span className="teo-edge-dock__avatar-wrap" aria-hidden>
          <AvatarImage
            className="teo-edge-dock__avatar"
            src={avatarUrl!}
            onError={() => setAvatarFailed(true)}
          />
        </span>
      ) : (
        <IconChat size={20} />
      )}
      {unreadCount > 0 ? (
        <span className="teo-edge-dock__badge" aria-label={`${unreadCount} unread`}>
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      ) : null}
    </button>
  );
}
